import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import CloseIcon from '@mui/icons-material/Close'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import {
  Box,
  Drawer,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { format } from 'date-fns'
import { cs, enUS } from 'date-fns/locale'

import { LessonCard } from '../../../components/LessonCard'
import { formatLessonDate, useLessonGroups } from '../../../components/LessonCard/utils'
import { toast } from '../../../components/LsToast'
import { useEscapeKey } from '../../../hooks/useEscapeKey'
import { Path } from '../../../router/BaseRouter/enums'
import { getLessonsByCustomer } from '../../../service/lessons.service'
import { CustomerDto, LessonDto } from '../../../service/types'
import { useAppDispatch } from '../../../store/hooks'
import { dayPlanActions } from '../../../store/slices/dayPlanSlice'

export const DRAWER_WIDTH = { sm: 420, xl: 540 }

type Props = {
  open: boolean
  onClose: () => void
  customer: CustomerDto | null
}

type FilterMode = 'upcoming' | 'all'

export const CustomerLessonsDrawer: FC<Props> = ({ open, onClose, customer }) => {
  const { t, i18n } = useTranslation()
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const [lessons, setLessons] = useState<LessonDto[]>([])
  const [loading, setLoading] = useState(false)
  const [filterMode, setFilterMode] = useState<FilterMode>('upcoming')
  const dateLocale = i18n.language === 'cs' ? cs : enUS

  const loadLessons = useCallback(
    async (customerId: string) => {
      setLoading(true)
      try {
        const response = await getLessonsByCustomer(customerId)
        setLessons(response.data)
      } catch {
        toast.error(t('general.notification.unexpectedError'))
      } finally {
        setLoading(false)
      }
    },
    [t],
  )

  useEffect(() => {
    if (open && customer) {
      void loadLessons(customer.id)
    }
    if (!open) {
      setLessons([])
      setFilterMode('upcoming')
    }
  }, [open, customer, loadLessons])

  const today = format(new Date(), 'yyyy-MM-dd')

  const filteredLessons = useMemo(() => {
    if (filterMode === 'upcoming') {
      return lessons.filter((l) => l.date >= today)
    }
    return lessons
  }, [lessons, filterMode, today])

  const { groupedByDate, sortedDates } = useLessonGroups(filteredLessons)

  useEscapeKey(onClose, open)

  const handleOpenInDayPlan = (date: string, lessonId: string) => {
    dispatch(dayPlanActions.setDate(date))
    navigate(`${Path.DAY_PLAN}/${lessonId}`)
  }

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 2,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant="h6" noWrap sx={{ flex: 1 }}>
          {customer ? t('customers.lessons.title', { name: customer.name }) : ''}
        </Typography>
        {customer && (
          <IconButton size="small" onClick={() => navigate(`/customer/${customer.id}`)}>
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        )}
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Filter toggle */}
      <Box sx={{ px: 3, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <ToggleButtonGroup
          value={filterMode}
          exclusive
          onChange={(_, value) => value && setFilterMode(value)}
          size="small"
          fullWidth
        >
          <ToggleButton value="upcoming">{t('customers.lessons.upcoming')}</ToggleButton>
          <ToggleButton value="all">{t('customers.lessons.all')}</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Body */}
      <Box sx={{ flex: 1, overflow: 'auto', px: 3, py: 2 }}>
        {loading && (
          <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            ...
          </Typography>
        )}

        {!loading && filteredLessons.length === 0 && (
          <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            {t('customers.lessons.noLessons')}
          </Typography>
        )}

        {!loading &&
          sortedDates.map((date) => (
            <Box key={date} sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                {formatLessonDate(date, dateLocale)}
              </Typography>

              {groupedByDate[date].map((lesson) => (
                <LessonCard key={lesson.id} lesson={lesson} onOpenInDayPlan={handleOpenInDayPlan} />
              ))}
            </Box>
          ))}
      </Box>
    </Box>
  )

  if (isSmallScreen) {
    return (
      <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: '100%' } }}>
        {drawerContent}
      </Drawer>
    )
  }

  return (
    <Drawer
      variant="persistent"
      anchor="right"
      open={open}
      sx={{
        width: DRAWER_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          boxSizing: 'border-box',
          borderLeft: '1px solid',
          borderColor: 'divider',
        },
      }}
    >
      {drawerContent}
    </Drawer>
  )
}
